import type { ComponentType, ReactNode } from "react";
import {
  SearchFilterToolbarShell,
  searchFilterToolbarDefaultClassNames,
  type SearchFilterToolbarClassNamesOverride,
} from "../shared/SearchFilterToolbarShell";
import { FreelancerSearchFilterBadges } from "./FreelancerSearchFilterBadges";
import type { CityLike, ServiceLike } from "./FreelancerSearchFilterBadges";
import type {
  AlphabeticalSortOption,
  AllianceCountSortOption,
  FreelancerSearchFilterToolbarLabels,
  ModalSlotRenderProps,
  ToolbarBadgeProps,
  ToolbarButtonProps,
} from "./types";

export type { CityLike, ServiceLike };

export const freelancerSearchFilterToolbarDefaultClassNames =
  searchFilterToolbarDefaultClassNames;

export type FreelancerSearchFilterToolbarClassNames =
  SearchFilterToolbarClassNamesOverride & {
    badgeRow?: string;
    badgeFilters?: string;
    badgeSorts?: string;
  };

export type FreelancerSearchFilterToolbarProps<
  TService extends ServiceLike,
  TCity extends CityLike,
> = {
  onApplySearch: () => void | Promise<void>;
  labels: FreelancerSearchFilterToolbarLabels;
  getLanguageDisplayLabel: (code: string) => string;

  selectedLanguages: string[];
  selectedServices: TService[];
  selectedCities: TCity[];
  rating: number;
  proximity: number;
  alphabeticalSort: AlphabeticalSortOption;
  allianceCountSort: AllianceCountSortOption;

  onRemoveLanguage: (code: string) => void;
  onRemoveService: (id: number) => void;
  onRemoveCity: (id: number) => void;
  onClearRating: () => void;
  onClearProximity: () => void;
  onResetAlphabeticalSort: () => void;
  onResetAllianceCountSort: () => void;

  components: {
    Button: ComponentType<ToolbarButtonProps>;
    Badge: ComponentType<ToolbarBadgeProps>;
  };

  renderFilterModal: (props: ModalSlotRenderProps) => ReactNode;
  renderSortModal: (props: ModalSlotRenderProps) => ReactNode;

  classNames?: FreelancerSearchFilterToolbarClassNames;

  formatRatingValue?: (rating: number) => string;
  formatProximityValue?: (km: number) => string;
};

export function FreelancerSearchFilterToolbar<
  TService extends ServiceLike,
  TCity extends CityLike,
>({
  onApplySearch,
  labels,
  getLanguageDisplayLabel,
  selectedLanguages,
  selectedServices,
  selectedCities,
  rating,
  proximity,
  alphabeticalSort,
  allianceCountSort,
  onRemoveLanguage,
  onRemoveService,
  onRemoveCity,
  onClearRating,
  onClearProximity,
  onResetAlphabeticalSort,
  onResetAllianceCountSort,
  components: { Button, Badge },
  renderFilterModal,
  renderSortModal,
  classNames: classNamesProp,
  formatRatingValue,
  formatProximityValue,
}: FreelancerSearchFilterToolbarProps<TService, TCity>) {
  const { badgeRow, badgeFilters, badgeSorts, ...toolbarClassNames } =
    classNamesProp ?? {};

  return (
    <>
      <SearchFilterToolbarShell
        labels={{ filterBy: labels.filterBy, sortBy: labels.sortBy }}
        classNames={toolbarClassNames}
        Button={Button}
        renderFilterModal={renderFilterModal}
        renderSortModal={renderSortModal}
        onApplySearch={onApplySearch}
      />

      <FreelancerSearchFilterBadges
        labels={labels}
        getLanguageDisplayLabel={getLanguageDisplayLabel}
        selectedLanguages={selectedLanguages}
        selectedServices={selectedServices}
        selectedCities={selectedCities}
        rating={rating}
        proximity={proximity}
        alphabeticalSort={alphabeticalSort}
        allianceCountSort={allianceCountSort}
        onRemoveLanguage={onRemoveLanguage}
        onRemoveService={onRemoveService}
        onRemoveCity={onRemoveCity}
        onClearRating={onClearRating}
        onClearProximity={onClearProximity}
        onResetAlphabeticalSort={onResetAlphabeticalSort}
        onResetAllianceCountSort={onResetAllianceCountSort}
        Badge={Badge}
        badgeRowClassName={badgeRow}
        badgeFiltersClassName={badgeFilters}
        badgeSortsClassName={badgeSorts}
        formatRatingValue={formatRatingValue}
        formatProximityValue={formatProximityValue}
      />
    </>
  );
}
